<script>
/**
 * 카테고리 등록/수정 공용 모달 컴포넌트.
 *
 * Props:
 *   modelValue  - v-model: 다이얼로그 표시 여부
 *   editData    - 수정 시 전달할 카테고리 데이터 (null이면 등록 모드)
 *
 * Emits:
 *   update:modelValue - 다이얼로그 닫힘
 *   saved             - 등록 또는 수정 완료 후 부모에 통지
 *   deleted           - 수정 모드에서 삭제 완료 후 부모에 통지
 */
import { createCategory, updateCategory, deleteCategory } from '@/api/category.js'

export default {
  name: 'CategoryFormDialog',

  props: {
    /** v-model: 다이얼로그 표시 여부 */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /**
     * 수정 모드일 때 기존 카테고리 데이터.
     * null이면 등록 모드로 동작한다.
     */
    editData: {
      type: Object,
      default: null,
    },
    /** HQ 관리자 여부 (삭제 버튼 노출용) */
    isHq: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'saved', 'deleted'],

  data() {
    return {
      /** 기본 결제구분 선택지 (API: CASH / CARD / MIXED) */
      productPaymentTypeOptions: [
        { title: '현금(계좌이체)', value: 'CASH' },
        { title: '카드결제', value: 'CARD' },
        { title: '현금 + 카드', value: 'MIXED' },
      ],
      /** 폼 데이터 */
      form: {
        categoryName: '',
        productPaymentType: 'MIXED',
        orderby: null,
      },
      /** 폼 유효성 검사 결과 */
      formValid: false,
      /** 저장 중 로딩 상태 */
      saving: false,
      /** 삭제 중 로딩 상태 */
      deleting: false,
      /** 오류 메시지 */
      errorMsg: '',
      /** 삭제 확인 다이얼로그 */
      deleteDialog: false,
    }
  },

  computed: {
    /** 수정 모드 여부 */
    isEditMode() {
      return !!this.editData
    },
    /** 다이얼로그 제목 */
    dialogTitle() {
      return this.isEditMode ? '카테고리 수정' : '카테고리 등록'
    },
    /** 다이얼로그 표시 여부 (v-model 연결) */
    visible: {
      get() { return this.modelValue },
      set(val) { this.$emit('update:modelValue', val) },
    },
  },

  watch: {
    /**
     * 다이얼로그가 열릴 때 폼 초기화.
     * 수정 모드이면 editData를 폼에 주입하고,
     * 등록 모드이면 빈 값으로 초기화한다.
     */
    modelValue(val) {
      if (val) {
        this.errorMsg = ''
        this.deleteDialog = false
        if (this.isEditMode) {
          this.form.categoryName = this.editData.categoryName || ''
          this.form.productPaymentType = ['CASH', 'CARD', 'MIXED'].includes(this.editData.productPaymentType)
            ? this.editData.productPaymentType
            : 'MIXED'
          this.form.orderby =
            this.editData.orderby === null || this.editData.orderby === undefined || this.editData.orderby === ''
              ? null
              : Number(this.editData.orderby)
        } else {
          this.form = { categoryName: '', productPaymentType: 'MIXED', orderby: null }
        }
      }
    },
  },

  methods: {
    /** 저장 버튼 클릭: 등록 또는 수정 API 호출 */
    async handleSave() {
      this.errorMsg = ''
      if (!this.form.categoryName?.trim()) {
        this.errorMsg = '카테고리명을 입력해 주세요.'
        return
      }
      if (this.form.orderby !== null && this.form.orderby !== '' && Number(this.form.orderby) < 0) {
        this.errorMsg = '정렬순서는 0 이상이어야 합니다.'
        return
      }

      this.saving = true
      try {
        const payload = {
          categoryName: this.form.categoryName.trim(),
          productPaymentType: 'MIXED',
          orderby:
            this.form.orderby === null || this.form.orderby === '' || this.form.orderby === undefined
              ? null
              : Number(this.form.orderby),
        }

        let res
        if (this.isEditMode) {
          res = await updateCategory(this.editData.categoryCode, payload)
        } else {
          res = await createCategory(payload)
        }

        if (res.data?.code === 0) {
          this.$emit('saved', res.data)
          this.visible = false
        } else {
          this.errorMsg = res.data?.message || '저장 중 오류가 발생했습니다.'
        }
      } catch (e) {
        console.error('[CategoryFormDialog] handleSave error:', e)
        this.errorMsg = '저장 중 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },

    /** 취소 버튼 클릭: 다이얼로그 닫기 */
    handleClose() {
      this.visible = false
    },

    /** 삭제 버튼 클릭: 확인 다이얼로그 오픈 */
    openDeleteConfirm() {
      this.deleteDialog = true
      this.errorMsg = ''
    },

    /** 삭제 확인 후 실행 */
    async handleDelete() {
      if (!this.editData?.categoryCode) return
      this.deleting = true
      this.errorMsg = ''
      try {
        const res = await deleteCategory(this.editData.categoryCode)
        if (res.data?.code === 0) {
          this.deleteDialog = false
          this.visible = false
          this.$emit('deleted')
        } else {
          this.errorMsg = res.data?.message || '삭제 중 오류가 발생했습니다.'
        }
      } catch (e) {
        console.error('[CategoryFormDialog] handleDelete error:', e)
        this.errorMsg = '삭제 중 오류가 발생했습니다.'
      } finally {
        this.deleting = false
      }
    },
  },
}
</script>

<template>
  <v-dialog v-model="visible" max-width="480" persistent>
    <v-card rounded="lg">
      <!-- 헤더 -->
      <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3">
        {{ dialogTitle }}
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-5">
        <!-- 오류 메시지 -->
        <v-alert
          v-if="errorMsg"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ errorMsg }}
        </v-alert>

        <v-row dense>
          <!-- 카테고리명 -->
          <v-col cols="12">
            <v-text-field
              v-model="form.categoryName"
              label="카테고리명 *"
              placeholder="카테고리명을 입력하세요"
              variant="outlined"
              density="compact"
              maxlength="255"
              counter
              :rules="[v => !!v?.trim() || '카테고리명은 필수입니다.']"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model.number="form.orderby"
              label="정렬순서"
              type="number"
              min="0"
              step="1"
              variant="outlined"
              density="compact"
              hide-details
              placeholder="작을수록 먼저 노출 (미입력 시 기본값)"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <!-- 액션 버튼 -->
      <v-card-actions class="pa-4 justify-space-between">
        <div>
          <v-btn
            v-if="isEditMode && isHq"
            color="error"
            variant="text"
            :loading="deleting"
            prepend-icon="mdi-delete-outline"
            @click="openDeleteConfirm"
          >
            삭제
          </v-btn>
        </div>
        <div class="d-flex ga-2">
          <v-btn variant="text" @click="handleClose">취소</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="saving"
            @click="handleSave"
          >
            {{ isEditMode ? '수정' : '등록' }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>

    <!-- 삭제 확인 다이얼로그 -->
    <v-dialog v-model="deleteDialog" max-width="400" persistent>
      <v-card rounded="lg">
        <v-card-title class="text-subtitle-1 font-weight-bold pa-5 pb-3">
          카테고리 삭제
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-5">
          <v-alert
            v-if="errorMsg"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-4"
          >
            {{ errorMsg }}
          </v-alert>
          <p v-else class="text-body-2">
            <strong>{{ form.categoryName || editData?.categoryName }}</strong> 카테고리를 삭제하시겠습니까?<br>
            <span class="text-grey text-caption">삭제된 카테고리는 복구할 수 없습니다.</span>
          </p>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4 justify-end ga-2">
          <v-btn variant="text" @click="deleteDialog = false; errorMsg = ''">취소</v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="deleting"
            @click="handleDelete"
          >
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>
